import { handleMutation } from '@/helpers/toastHelper';
import { useCallback, useMemo } from 'react';
import { sortSimpleObjectArray } from '../../helpers/helper';
import {
  CreateSkill,
  ReadSkills,
  UpdateSkill,
  useCreateSkillsMutation,
  useDeleteSkillsMutation,
  useGetSkillsQuery,
  useUpdateSkillsMutation
} from '../gql/graphql.generated';
import { SkillType } from './skillSettings.types';

export const useSkills = () => {
  const [{ data }] = useGetSkillsQuery();

  const skills = useMemo(() => {
    return sortSimpleObjectArray(gqlToSkills(data?.skills || []), 'name') as SkillType[];
  }, [data]);

  return skills;
};

export const useActiveSkills = () => {
  const skills = useSkills();

  return useMemo(() => skills.filter(s => s.isActive), [skills]);
};

export const useGetSkill = () => {
  const skills = useSkills();

  return useCallback(
    (id: string) => {
      const skill = skills.find(s => s.id === id);

      return skill;
    },
    [skills]
  );
};

export const useCreateSkills = () => {
  const [responseData, create] = useCreateSkillsMutation();

  const createSkills = useCallback(
    async (skills: SkillType[]) => {
      return create({ skills: skillsToGQL(skills) });
    },
    [create]
  );

  return { fetching: responseData.fetching, create: createSkills };
};

export const useUpdateSkills = () => {
  const [responseData, update] = useUpdateSkillsMutation();

  const updateSkills = useCallback(
    async (skills: SkillType[]) => {
      return update({ skills: skillsToGQL(skills, true) as UpdateSkill[] });
    },
    [update]
  );

  return { fetching: responseData.fetching, update: updateSkills };
};

export const useDeleteSkills = () => {
  const [{ fetching }, deleteSkillsBulk] = useDeleteSkillsMutation();

  const deleteSkills = useCallback(
    async (skillIds: string[]) => {
      const success = await handleMutation(() => deleteSkillsBulk({ skillIds }), {
        successMessage: 'Skills deleted successfully.',
        errorMessage: 'Failed to delete skills.',
        customSuccessCheck: data => data?.deleteSkills === true
      });
      return { error: success ? undefined : new Error('Failed to delete skills') };
    },
    [deleteSkillsBulk]
  );

  return { deleteSkills, fetching };
};

export const skillsToGQL = (skills: SkillType[], isUpdate?: boolean): CreateSkill[] | UpdateSkill[] => {
  return skills.map(s => {
    if (!isUpdate) {
      return {
        name: s.name,
        active: s.isActive,
        properties: JSON.stringify({ tooltip: s.tooltip })
      };
    }

    return {
      id: s.id,
      name: s.name,
      active: s.isActive,
      properties: JSON.stringify({ tooltip: s.tooltip })
    };
  });
};

export const gqlToSkills = (skills: ReadSkills[]): SkillType[] => {
  return skills.map(s => {
    let props: any = {};
    if (s.properties) {
      props = JSON.parse(s.properties);
    }
    return {
      id: s.id,
      name: s.name,
      isActive: !!s.active,
      tooltip: props.tooltip || ''
    };
  });
};
